/**
 * Generic optimistic locking hook with built-in version state management.
 *
 * This hook provides complete optimistic locking functionality for any resource
 * that implements version control. It manages version state, conflict detection,
 * and provides utilities for conflict resolution.
 *
 * @example
 * ```typescript
 * const {
 *   currentVersion,
 *   updateWithLocking,
 *   conflictState,
 *   handleRefresh,
 *   handleForceOverwrite,
 *   clearConflict
 * } = useOptimisticLocking({
 *   initialVersion: source.version,
 *   resourceId: source.id,
 *   apiRead: (id) => sourcesApi.read({ id, token }),
 *   apiUpdate: (id, data) => sourcesApi.update({ id, data, token }),
 *   onUpdateSuccess: () => toast({ title: 'Updated!' }),
 * });
 * ```
 */

import { useState, useCallback } from "react";
import { AxiosError, AxiosResponse } from "axios";
import {
  FieldDiff,
  generateFieldDiffs,
  DiffConfig,
} from "@/lib/fieldDiffUtils";

interface ConflictError {
  version?: string;
  current_version?: number;
  conflict?: boolean;
  message?: string;
}

interface UpdateResult<T> {
  success: boolean;
  conflict?: boolean;
  message?: string;
  currentVersion?: number;
  data?: T;
  error?: string;
}

interface ConflictState<T = any> {
  isOpen: boolean;
  latestVersion: number | null;
  errorMessage: string | null;
  fieldDiffs?: FieldDiff[];
  latestData?: T;
}

interface UseOptimisticLockingOptions<T, TUpdateData = any> {
  /** Initial version number of the resource */
  initialVersion: number;

  /** Resource ID for refresh operations */
  resourceId: string | number;

  /** Function to read/refresh the resource from API */
  apiRead: (id: string | number) => Promise<AxiosResponse<T>>;

  /** Function to update the resource via API */
  apiUpdate: (
    id: string | number,
    data: TUpdateData,
  ) => Promise<AxiosResponse<T>>;

  /** Optional: Callback when update succeeds */
  onUpdateSuccess?: (data: T) => void;

  /** Optional: Callback when update fails (non-conflict) */
  onUpdateError?: (error: string) => void;

  /** Optional: Callback when conflict is detected */
  onConflict?: (conflictData: ConflictError) => void;

  /** Optional: Callback after successful refresh */
  onRefreshSuccess?: (data: T) => void;

  /** Optional: Transform function to extract version from response */
  getVersionFromResponse?: (data: T) => number;

  /** Optional: Resource name for error messages (defaults to 'Resource') */
  resourceName?: string;

  /** Optional: Configuration for field diff generation */
  diffConfig?: DiffConfig;

  /** Optional: Local data for comparison when conflict occurs */
  localData?: T;
}

export function useOptimisticLocking<
  T extends { version: number },
  TUpdateData = any,
>({
  initialVersion,
  resourceId,
  apiRead,
  apiUpdate,
  onUpdateSuccess,
  onUpdateError,
  onConflict,
  onRefreshSuccess,
  getVersionFromResponse = (data) => data.version,
  resourceName = "Resource",
  diffConfig,
  localData,
}: UseOptimisticLockingOptions<T, TUpdateData>) {
  // Version state management
  const [currentVersion, setCurrentVersion] = useState<number>(initialVersion);

  // Conflict state management
  const [conflictState, setConflictState] = useState<ConflictState<T>>({
    isOpen: false,
    latestVersion: null,
    errorMessage: null,
  });

  // Loading state
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  /**
   * Update resource with optimistic locking
   */
  const updateWithLocking = useCallback(
    async (updateData: TUpdateData): Promise<UpdateResult<T>> => {
      setIsUpdating(true);

      try {
        const response = await apiUpdate(resourceId, updateData);
        const newVersion = getVersionFromResponse(response.data);

        setCurrentVersion(newVersion);
        onUpdateSuccess?.(response.data);

        return {
          success: true,
          data: response.data,
          currentVersion: newVersion,
        };
      } catch (err) {
        const axiosError = err as AxiosError<ConflictError>;

        // Check for optimistic locking conflict
        if (axiosError.response?.data?.conflict) {
          const conflictData = axiosError.response.data;

          // Generate field diffs if local data and diff config are provided
          let fieldDiffs: FieldDiff[] | undefined;
          let latestData: T | undefined;

          if (localData && diffConfig) {
            try {
              // Fetch latest data to compare with local data
              const latestResponse = await apiRead(resourceId);
              latestData = latestResponse.data;
              fieldDiffs = generateFieldDiffs(
                localData,
                latestData,
                diffConfig,
              );
            } catch (error) {
              console.warn(
                "Failed to fetch latest data for field diff:",
                error,
              );
            }
          }

          setConflictState({
            isOpen: true,
            latestVersion: conflictData.current_version || null,
            errorMessage:
              conflictData.version || conflictData.message || resourceName,
            fieldDiffs,
            latestData,
          });

          onConflict?.(conflictData);

          return {
            success: false,
            conflict: true,
            message: conflictData.version,
            currentVersion: conflictData.current_version,
          };
        }

        // Handle other errors
        const errorMessage =
          (axiosError.response?.data as any)?.message ||
          axiosError.message ||
          resourceName;

        onUpdateError?.(errorMessage);

        return {
          success: false,
          conflict: false,
          error: errorMessage,
        };
      } finally {
        setIsUpdating(false);
      }
    },
    [
      resourceId,
      apiUpdate,
      resourceName,
      getVersionFromResponse,
      onUpdateSuccess,
      onConflict,
      onUpdateError,
      apiRead,
      diffConfig,
      localData,
    ],
  );

  /**
   * Refresh resource from server (discards local changes)
   */
  const handleRefresh = useCallback(async (): Promise<T | null> => {
    setIsRefreshing(true);

    try {
      const response = await apiRead(resourceId);
      const newVersion = getVersionFromResponse(response.data);

      setCurrentVersion(newVersion);
      setConflictState({
        isOpen: false,
        latestVersion: null,
        errorMessage: null,
      });

      onRefreshSuccess?.(response.data);

      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to refresh resource:", error);
      return null;
    } finally {
      setIsRefreshing(false);
    }
  }, [resourceId, apiRead, getVersionFromResponse, onRefreshSuccess]);

  /**
   * Force overwrite with local changes using latest version
   */
  const handleForceOverwrite = useCallback(
    async (updateData: TUpdateData): Promise<UpdateResult<T>> => {
      if (!conflictState.latestVersion) {
        return {
          success: false,
          error: resourceName,
        };
      }

      setIsUpdating(true);

      try {
        // Use the server's current version for overwrite
        const response = await apiUpdate(resourceId, updateData);
        const newVersion = getVersionFromResponse(response.data);

        setCurrentVersion(newVersion);
        setConflictState({
          isOpen: false,
          latestVersion: null,
          errorMessage: null,
        });

        onUpdateSuccess?.(response.data);

        return {
          success: true,
          data: response.data,
          currentVersion: newVersion,
        };
      } catch (error) {
        const errorMessage = (error as Error).message || resourceName;
        onUpdateError?.(errorMessage);

        return {
          success: false,
          error: errorMessage,
        };
      } finally {
        setIsUpdating(false);
      }
    },
    [
      conflictState.latestVersion,
      resourceId,
      resourceName,
      apiUpdate,
      getVersionFromResponse,
      onUpdateSuccess,
      onUpdateError,
    ],
  );

  /**
   * Clear conflict state
   */
  const clearConflict = useCallback(() => {
    setConflictState({
      isOpen: false,
      latestVersion: null,
      errorMessage: null,
      fieldDiffs: undefined,
      latestData: undefined,
    });
  }, []);

  /**
   * Update current version (useful when loading fresh data)
   */
  const updateVersion = useCallback((newVersion: number) => {
    setCurrentVersion(newVersion);
  }, []);

  return {
    // Version state
    currentVersion,
    updateVersion,

    // Update operations
    updateWithLocking,
    isUpdating,

    // Conflict management
    conflictState,
    clearConflict,

    // Conflict resolution
    handleRefresh,
    handleForceOverwrite,
    isRefreshing,
  };
}
